import React, { useState, useEffect, useMemo } from 'react';
import { Trophy, Loader2, Clock, EyeOff, RefreshCw } from 'lucide-react';
import { apiClient } from '../services/api';
import { ReviewedCard, UnreviewedCard } from '../types/api';
import { Card } from '../types/api';
import { CardDetail } from './CardDetail';
import { useInstance } from '../hooks/useInstance';
import { useAuth } from '../hooks/useAuth';

interface InsightsRowProps {
  instanceId: number | null;
}

const TOP_N = 5;

interface WidgetState<T> {
  items: T[];
  isLoading: boolean;
  error: string | null;
}

function useWidgetData<T>(fetch: () => Promise<T[]>, deps: React.DependencyList): WidgetState<T> {
  const [state, setState] = useState<WidgetState<T>>({ items: [], isLoading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    setState(s => ({ ...s, isLoading: true, error: null }));
    fetch()
      .then(items => { if (!cancelled) setState({ items, isLoading: false, error: null }); })
      .catch((e: any) => { if (!cancelled) setState({ items: [], isLoading: false, error: e?.response?.data?.detail ?? e?.message ?? 'Failed to load' }); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

function WidgetShell({ icon, title, action, children }: { icon: React.ReactNode; title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex-1 min-w-[220px]">
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-gray-100">
        {icon}
        <h2 className="text-sm font-semibold text-gray-900 flex-1">{title}</h2>
        {action}
      </div>
      <div className="px-4 py-3">
        <div className="max-h-52 overflow-y-auto pr-0.5">{children}</div>
      </div>
    </div>
  );
}

function WidgetBody({ isLoading, error, empty, children }: { isLoading: boolean; error: string | null; empty: boolean; children: React.ReactNode }) {
  if (isLoading) return <div className="flex items-center justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-gray-400" /></div>;
  if (error) return <p className="text-xs text-red-400 py-4 text-center">{error}</p>;
  if (empty) return <p className="text-xs text-gray-400 py-4 text-center">Nothing to show yet.</p>;
  return <>{children}</>;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return mins <= 1 ? 'just now' : `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export const InsightsRow: React.FC<InsightsRowProps> = ({ instanceId }) => {
  const { instancesWithRoles } = useInstance();
  const { user } = useAuth();
  const [viewingCard, setViewingCard] = useState<{ id: number; name: string } | null>(null);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);

  const isInstanceOwner = useMemo(() => {
    if (!instanceId || !user) return false;
    const found = instancesWithRoles.find(i => i.instance.id === instanceId);
    return found?.user_role === 'owner' || found?.instance.created_by === user.id;
  }, [instanceId, instancesWithRoles, user]);

  useEffect(() => {
    if (!instanceId || !isInstanceOwner) return;
    apiClient.getCategories(instanceId)
      .then(cats => setCategories(cats.map(c => ({ id: c.id, name: c.name }))))
      .catch(() => {});
  }, [instanceId, isInstanceOwner]);

  const ranking = useWidgetData<ReviewedCard>(
    () => apiClient.getReviewedCards({ sort_by: 'review_count', days: 7, ...(instanceId ? { instance_id: instanceId } : {}) })
      .then(d => d.items.filter(c => !c.is_folder).slice(0, TOP_N)),
    [instanceId]
  );

  const recent = useWidgetData<Card>(
    () => apiClient.getRecentCards({ ...(instanceId ? { instance_id: instanceId } : {}), limit: TOP_N * 4 })
      .then(d => d.items.filter(c => !c.is_folder).slice(0, TOP_N)),
    [instanceId]
  );

  const [unreviewedOffset, setUnreviewedOffset] = useState(0);

  const unreviewed = useWidgetData<UnreviewedCard>(
    () => apiClient.getUnreviewedCards(instanceId ?? undefined, TOP_N * 4)
      .then(d => d.items),
    [instanceId]
  );

  const unreviewedPage = useMemo(
    () => unreviewed.items.slice(unreviewedOffset, unreviewedOffset + TOP_N),
    [unreviewed.items, unreviewedOffset]
  );

  const cycleUnreviewed = () =>
    setUnreviewedOffset(off => (off + TOP_N) >= unreviewed.items.length ? 0 : off + TOP_N);

  const cardRow = (id: number, name: string, sub?: string | null) => (
    <li key={id}>
      <button
        onClick={() => setViewingCard({ id, name })}
        className="w-full flex items-center gap-2 text-left hover:bg-gray-50 rounded-lg -mx-1 px-1 py-1 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-800 truncate">{name}</p>
          {sub && <p className="text-xs text-gray-400 truncate">{sub}</p>}
        </div>
      </button>
    </li>
  );

  return (
    <>
      <div className="-mx-4 sm:mx-0 overflow-x-auto">
        <div className="flex gap-3 px-4 sm:px-0 pb-1" style={{ minWidth: 'max-content', width: '100%' }}>

          {/* Most Reviewed This Week */}
          <WidgetShell icon={<Trophy className="h-4 w-4 text-amber-500" />} title="Most Practiced (Last 7 Days)">
            <WidgetBody isLoading={ranking.isLoading} error={ranking.error} empty={ranking.items.length === 0}>
              <ol className="space-y-0.5">
                {ranking.items.map((card, i) => (
                  <li key={card.id}>
                    <button
                      onClick={() => setViewingCard({ id: card.id, name: card.name })}
                      className="w-full flex items-center gap-3 text-left hover:bg-gray-50 rounded-lg -mx-1 px-1 py-1 transition-colors"
                    >
                      <span className="text-xs font-bold text-gray-400 w-5 flex-shrink-0 text-right">#{i + 1}</span>
                      <span className="text-sm text-gray-800 flex-1 truncate">{card.name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">
                        {card.review_count} {card.review_count === 1 ? 'review' : 'reviews'}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            </WidgetBody>
          </WidgetShell>

          {/* Recently Created */}
          <WidgetShell icon={<Clock className="h-4 w-4 text-blue-500" />} title="Recently Created">
            <WidgetBody isLoading={recent.isLoading} error={recent.error} empty={recent.items.length === 0}>
              <ul className="space-y-0.5">
                {recent.items.map(card => cardRow(card.id, card.name, relativeTime(card.created_at)))}
              </ul>
            </WidgetBody>
          </WidgetShell>

          {/* Not Reviewed Yet */}
          <WidgetShell
            icon={<EyeOff className="h-4 w-4 text-orange-500" />}
            title="Not Reviewed Yet"
            action={unreviewed.items.length > TOP_N && (
              <button
                onClick={cycleUnreviewed}
                className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                title="Show different cards"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            )}
          >
            <WidgetBody isLoading={unreviewed.isLoading} error={unreviewed.error} empty={unreviewed.items.length === 0}>
              <ul className="space-y-0.5">
                {unreviewedPage.map(card => cardRow(card.id, card.name, card.category_name))}
              </ul>
            </WidgetBody>
          </WidgetShell>

        </div>
      </div>

      {viewingCard && (
        <CardDetail
          cardId={viewingCard.id}
          onClose={() => setViewingCard(null)}
          onSave={() => {}}
          isInstanceOwner={isInstanceOwner}
          onDelete={isInstanceOwner ? () => setViewingCard(null) : undefined}
          onMove={isInstanceOwner ? async (itemId, data) => { await apiClient.moveCard(itemId, data); } : undefined}
          categories={categories}
        />
      )}
    </>
  );
};
